import React, { useState, useCallback, useMemo } from 'react'
import { formatCurrency, formatQuantity } from '../../utils/formatters'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  TextField,
  Grid,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  FormControl,
  InputLabel,
  Select
} from '@mui/material'
import {
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Print as PrintIcon,
  AttachMoney as MoneyIcon,
  PauseCircleOutline as PendingIcon
} from '@mui/icons-material'
import { format } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { useLoadForms, useDeleteLoadForm, useBookers, usePrintLoadForm, usePrintLoadFormWithInvoices, useViewInvoicesOnly, useMarkLoadFormAsPending } from '../../hooks/api/useLoadForms'
import { useAreas } from '../../hooks/api/useAreas'
import { LoadForm } from '../../types'
import StatusChip from '../../components/ui/StatusChip'

const LoadFormListPage: React.FC = () => {
  const navigate = useNavigate()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [bookerFilter, setBookerFilter] = useState<string>('')
  const [areaFilter, setAreaFilter] = useState<string>('')
  const [dateFilter, setDateFilter] = useState<string>('')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedLoadForm, setSelectedLoadForm] = useState<LoadForm | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [loadFormToDelete, setLoadFormToDelete] = useState<string | null>(null)

  // Applied filters (what actually gets sent to API)
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('')
  const [appliedStatusFilter, setAppliedStatusFilter] = useState<string>('')
  const [appliedBookerFilter, setAppliedBookerFilter] = useState<string>('')
  const [appliedAreaFilter, setAppliedAreaFilter] = useState<string>('')
  const [appliedDateFilter, setAppliedDateFilter] = useState<string>('')

  // Memoized event handlers to prevent unnecessary re-renders
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }, [])

  const handleStatusChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setStatusFilter(e.target.value)
  }, [])

  const handleBookerChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setBookerFilter(e.target.value)
  }, [])

  const handleAreaChange = useCallback((e: any) => {
    setAreaFilter(e.target.value)
  }, [])

  const handleDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDateFilter(e.target.value)
  }, [])

  const handleSearch = useCallback(() => {
    setAppliedSearchTerm(searchTerm)
    setAppliedStatusFilter(statusFilter)
    setAppliedBookerFilter(bookerFilter)
    setAppliedAreaFilter(areaFilter)
    setAppliedDateFilter(dateFilter)
    setPage(0) // Reset to first page when searching
  }, [searchTerm, statusFilter, bookerFilter, areaFilter, dateFilter])

  const handleClearFilters = useCallback(() => {
    setSearchTerm('')
    setStatusFilter('')
    setBookerFilter('')
    setAreaFilter('')
    setDateFilter('')
    setAppliedSearchTerm('')
    setAppliedStatusFilter('')
    setAppliedBookerFilter('')
    setAppliedAreaFilter('')
    setAppliedDateFilter('')
    setPage(0)
  }, [])

  const { data: bookersData } = useBookers()
  const { data: areasData } = useAreas()
  const { data: loadFormsData, isLoading, error, refetch, isFetching } = useLoadForms({
    page: page + 1,
    limit: rowsPerPage,
    search: appliedSearchTerm || undefined,
    status: appliedStatusFilter || undefined,
    bookerId: appliedBookerFilter || undefined,
    area: appliedAreaFilter || undefined,
    date: appliedDateFilter || undefined
  })
  const deleteMutation = useDeleteLoadForm()
  const printLoadFormMutation = usePrintLoadForm()
  const printLoadFormWithInvoicesMutation = usePrintLoadFormWithInvoices()
  const viewInvoicesOnlyMutation = useViewInvoicesOnly()
  const markAsPendingMutation = useMarkLoadFormAsPending()

  const loadForms = loadFormsData?.results || []
  const total = loadFormsData?.info?.total || 0
  const bookers = bookersData?.results || []
  const areas = areasData?.results || []

  // Memoized filter section to prevent re-renders
  const FilterSection = useMemo(() => (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            placeholder="Search load forms..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            select
            label="Status"
            value={statusFilter}
            onChange={handleStatusChange}
          >
            <MenuItem value="">All Status</MenuItem>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            select
            label="Booker"
            value={bookerFilter}
            onChange={handleBookerChange}
          >
            <MenuItem value="">All Bookers</MenuItem>
            {bookers.map((booker) => (
              <MenuItem key={booker._id} value={booker._id}>
                {booker.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={2}>
          <FormControl fullWidth>
            <InputLabel>Area</InputLabel>
            <Select
              value={areaFilter}
              onChange={handleAreaChange}
              label="Area"
            >
              <MenuItem value="">All Areas</MenuItem>
              {areas.map((area) => (
                <MenuItem key={area._id} value={area._id}>
                  {area.area_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            type="date"
            label="Date"
            value={dateFilter}
            onChange={handleDateChange}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid item xs={12} md={1}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSearch}
            startIcon={<SearchIcon />}
            disabled={isFetching}
          >
            Search
          </Button>
        </Grid>
      </Grid>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {total} total load forms
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={handleClearFilters}
        >
          Clear Filters
        </Button>
      </Box>
    </Paper>
  ), [searchTerm, statusFilter, bookerFilter, areaFilter, dateFilter, bookers, areas, total, handleSearchChange, handleStatusChange, handleBookerChange, handleAreaChange, handleDateChange, handleSearch, handleClearFilters, isFetching])

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, loadForm: LoadForm) => {
    setAnchorEl(event.currentTarget)
    setSelectedLoadForm(loadForm)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedLoadForm(null)
  }

  const handleSettle = (loadFormId: string) => {
    navigate(`/loadforms/${loadFormId}/settlement`)
  }

  const handlePrint = () => {
    if (selectedLoadForm) {
      printLoadFormMutation.mutate((selectedLoadForm as any)._id)
      handleMenuClose()
    }
  }

  const handlePrintWithInvoices = () => {
    if (selectedLoadForm) {
      printLoadFormWithInvoicesMutation.mutate((selectedLoadForm as any)._id)
      handleMenuClose()
    }
  }

  const handleViewInvoices = () => {
    if (selectedLoadForm) {
      viewInvoicesOnlyMutation.mutate((selectedLoadForm as any)._id)
      handleMenuClose()
    }
  }

  const handleMarkAsPending = async () => {
    if (selectedLoadForm) {
      try {
        await markAsPendingMutation.mutateAsync({ 
          id: (selectedLoadForm as any)._id,
          reason: 'Bills not delivered yet'
        })
        handleMenuClose()
      } catch (error) {
        console.error('Error marking load form as pending:', error)
      }
    }
  }

  const handleDeleteClick = () => {
    if (selectedLoadForm) {
      setLoadFormToDelete((selectedLoadForm as any)._id)
      setDeleteDialogOpen(true)
    }
    handleMenuClose()
  }

  const handleDeleteConfirm = async () => {
    if (loadFormToDelete) {
      try {
        await deleteMutation.mutateAsync(loadFormToDelete)
        setDeleteDialogOpen(false)
        setLoadFormToDelete(null)
      } catch (error) {
        console.error('Error deleting load form:', error)
      }
    }
  }

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false)
    setLoadFormToDelete(null)
  }, [])

  const getStatusVariant = useCallback((status: string): 'info' | 'warn' | 'neutral' => {
    switch (status) {
      case 'open':    return 'info'
      case 'pending': return 'warn'
      case 'closed':  return 'neutral'
      default:        return 'neutral'
    }
  }, [])

  // Memoized table rows to prevent unnecessary re-renders
  const TableRows = useMemo(() => (
    loadForms.map((loadForm: any) => (
      <TableRow
        key={loadForm._id}
        hover
        onClick={() => navigate(`/loadforms/${loadForm._id}`)}
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault()
            navigate(`/loadforms/${loadForm._id}`)
          }
        }}
        sx={{ cursor: 'pointer', '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 } }}
      >
        <TableCell>
          <Typography variant="body2" fontWeight="medium">
            {loadForm.booker_id?.name || 'N/A'}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight="medium">
            Rs {formatCurrency(loadForm.total_amount || 0)}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {formatQuantity(loadForm.total_quantity)}
          </Typography>
        </TableCell>
        <TableCell>
          <StatusChip
            label={(loadForm.status || 'unknown').toUpperCase()}
            variant={getStatusVariant(loadForm.status)}
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {loadForm.area_id?.area_name || 'N/A'}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {loadForm.createdAt ? format(new Date(loadForm.createdAt), 'MMM dd, yyyy') : 'N/A'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {loadForm.createdAt ? format(new Date(loadForm.createdAt), 'HH:mm') : ''}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {loadForm.updatedAt ? format(new Date(loadForm.updatedAt), 'MMM dd, yyyy') : 'N/A'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {loadForm.updatedAt ? format(new Date(loadForm.updatedAt), 'HH:mm') : ''}
          </Typography>
        </TableCell>
        <TableCell align="right" onClick={(event) => event.stopPropagation()}>
          <Box display="flex" gap={1} justifyContent="flex-end">
            <Tooltip title="Settle load form">
              <IconButton
                onClick={() => handleSettle(loadForm._id)}
                size="small"
                color="success"
              >
                <MoneyIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="More actions">
              <IconButton
                onClick={(e) => handleMenuOpen(e, loadForm)}
                size="small"
              >
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>
    ))
  ), [loadForms, getStatusVariant, navigate])

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error loading load forms. Please try again.
        </Alert>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Load Forms
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => refetch()}
          disabled={isLoading}
        >
          Refresh
        </Button>
      </Box>

      {FilterSection}

      <TableContainer component={Paper}>
        {isFetching && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Booker Name</TableCell>
              <TableCell>Total Amount</TableCell>
              <TableCell>Total Quantity</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Area</TableCell>
              <TableCell>Created Date</TableCell>
              <TableCell>Updated Date</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {TableRows}
          </TableBody>
        </Table>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewInvoices} disabled={viewInvoicesOnlyMutation.isPending}>
          <ViewIcon sx={{ mr: 1 }} />
          {viewInvoicesOnlyMutation.isPending ? 'Loading...' : 'View Invoices'}
        </MenuItem>
        <MenuItem onClick={handlePrint} disabled={printLoadFormMutation.isPending}>
          <PrintIcon sx={{ mr: 1 }} />
          {printLoadFormMutation.isPending ? 'Printing...' : 'Print'}
        </MenuItem>
        <MenuItem onClick={handlePrintWithInvoices} disabled={printLoadFormWithInvoicesMutation.isPending}>
          <PrintIcon sx={{ mr: 1 }} />
          {printLoadFormWithInvoicesMutation.isPending ? 'Printing...' : 'Print with Invoices'}
        </MenuItem>
        {selectedLoadForm?.status === 'open' && (
          <MenuItem onClick={handleMarkAsPending} disabled={markAsPendingMutation.isPending}>
            <PendingIcon sx={{ mr: 1 }} />
            {markAsPendingMutation.isPending ? 'Marking...' : 'Mark as Pending'}
          </MenuItem>
        )}
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Load Form</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this load form? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default LoadFormListPage
